// MCP Security — Tool catalog page.
//
// Shows every tool across the registered MCP endpoints on this site —
// name, rate class, rpm budget, OAuth scopes required, calls / p95 /
// last-called / status / schema hash. Metric strip up top; row click
// opens the tool detail drawer with the real 24 h series + callers.
//
// Wired 2026-10-11 to the MCP-UX routes:
//   GET /mcp/sites/:site_uid/endpoints   — tabs for endpoint picker
//   GET /mcp/sites/:site_uid/tools?endpoint=:id
//   GET /mcp/sites/:site_uid/tools/:name?endpoint=:id

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  Box, Stack, Typography, Alert, Chip, Button, LinearProgress,
} from "@mui/material";
import {
  Activity, Shield, User,
} from "lucide-react";
import copy from "copy-to-clipboard";

import axios from "../../../../../utils/axios/v1/userAxios";
import V2PageHeader from "../../../../common/V2PageHeader";
import SiteScopeGate from "../../../../common/SiteScopeGate";
import useGlobalSite from "../../../../../hooks/user/useGlobalSite";
import { useNotification } from "../../../../../contexts/NotificationContext";

import WafEventDrawerShell from "../../detection/WafEventDrawerShell";
import SmartParseDrawerHeader from "../../detection/v3/SmartParseDrawerHeader";
import { SmartParseSectionCard } from "../../detection/v3/smartParseDrawerUi";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";

import tokens from "./_shared/tokens";
import MetricCard from "./_shared/MetricCard";
import useMcpQuery from "./_shared/useMcpQuery";
import {
  primaryButtonStyle,
  secondaryButtonStyle,
  BORDER_COLOR,
} from "./_shared/buttonStyles";

// ── UI helpers ───────────────────────────────────────────────────────

const RATE_CLASS_STYLE = {
  low:    { bg: "#F3F4F6", fg: "#4B5563" },
  normal: { bg: "#E6F4EC", fg: "#1E6B3F" },
  high:   { bg: "#FFF4D6", fg: "#8A6410" },
  admin:  { bg: "#FBE8E8", fg: "#8E1F1F" },
  read:   { bg: "#E6F4EC", fg: "#1E6B3F" },
  write:  { bg: "#FFF4D6", fg: "#8A6410" },
};

const STATUS_STYLE = {
  active:            { bg: "#E6F4EC", fg: "#1E6B3F" },
  pending_approval:  { bg: "#FFF4D6", fg: "#8A6410" },
  denied:            { bg: "#FBE8E8", fg: "#8E1F1F" },
  removed:           { bg: "#F3F4F6", fg: "#4B5563" },
};

function fmtInt(n) {
  if (n == null) return "—";
  const v = Number(n);
  if (!isFinite(v)) return String(n);
  return v.toLocaleString();
}

function fmtMs(n) {
  if (n == null) return "—";
  const v = Number(n);
  if (!isFinite(v) || v <= 0) return "—";
  return `${Math.round(v)} ms`;
}

function fmtAgo(iso) {
  if (!iso) return "never";
  const t = new Date(iso).getTime();
  if (!t) return "—";
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s} s ago`;
  if (s < 3600) return `${Math.floor(s / 60)} m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  return `${Math.floor(s / 86400)} d ago`;
}

function shortHash(h) {
  if (!h) return "—";
  return h.length > 14 ? `${h.slice(0, 10)}…` : h;
}

// ── Detail drawer, pulled from GET /tools/:name ──────────────────────

function ToolDrawer({ open, siteUid, endpointId, toolName, onClose, onApproved }) {
  const skip = !siteUid || !endpointId || !toolName;
  const { data, error, loading, refetch } = useMcpQuery(
    skip ? null : `mcp/sites/${siteUid}/tools/${encodeURIComponent(toolName)}?endpoint=${endpointId}`,
    { skip },
  );
  const detail = data?.data || null;
  const { showNotification } = useNotification();
  const [approving, setApproving] = useState(false);

  const handleCopy = (value) => {
    if (!value || value === "—") return;
    copy(String(value));
  };

  const handleApprove = async () => {
    if (!endpointId || !toolName || approving) return;
    setApproving(true);
    try {
      await axios.post(
        `mcp/endpoints/${endpointId}/tools/${encodeURIComponent(toolName)}/approve`,
      );
      showNotification({ severity: "success", message: `${toolName}: allowed` });
      refetch && refetch();
      onApproved && onApproved();
    } catch (e) {
      showNotification({
        severity: "error",
        message: e?.response?.data?.message || e.message || "approve failed",
      });
    } finally {
      setApproving(false);
    }
  };

  // MCP_ROADMAP_UI_2026_11_03 — data-flow role + risk class labels.
  const [savingLabel, setSavingLabel] = useState(null);
  const setLabel = async (kind, value) => {
    if (!endpointId || !toolName || savingLabel) return;
    setSavingLabel(kind);
    try {
      await axios.post(
        `mcp/endpoints/${endpointId}/tools/${encodeURIComponent(toolName)}/${kind}`,
        kind === "flow_role" ? { flow_role: value } : { risk_class: value },
      );
      showNotification({ severity: "success", message: `${toolName}: ${kind.replace("_", " ")} → ${value}` });
      refetch && refetch();
      onApproved && onApproved();
    } catch (e) {
      showNotification({ severity: "error", message: e?.response?.data?.message || e.message || "update failed" });
    } finally {
      setSavingLabel(null);
    }
  };

  // 24 bars from `time_series` (ok + blocked share per hour bucket).
  const seriesBars = (() => {
    const ser = detail?.time_series || [];
    if (!ser.length) return Array.from({ length: 24 }, () => ({ ok: 0, blk: 0 }));
    const max = Math.max(...ser.map((r) => Number(r.ok || 0) + Number(r.blocked || 0)), 1);
    return ser.slice(-24).map((r) => {
      const total = Number(r.ok || 0) + Number(r.blocked || 0);
      const ok = total ? (Number(r.ok || 0) / max) * 100 : 0;
      const blk = total ? (Number(r.blocked || 0) / max) * 100 : 0;
      return { ok, blk };
    });
  })();

  const callers = detail?.last_callers || [];
  const totalAttackHits = (detail?.attack_classes || []).reduce((s, a) => s + Number(a.hits || 0), 0) || 1;
  const attacks = (detail?.attack_classes || []).map((a) => ({
    name: a.attack_class,
    v: Number(a.hits || 0),
    w: `${Math.round((Number(a.hits || 0) * 100) / totalAttackHits)}%`,
  }));

  const rateClass = detail?.rate_class || "normal";
  const toolStatus = detail?.status || (detail?.denied ? "denied" : "active");

  // Header — WAF-style hero mark + title/method/path grouping.
  const header = toolName ? (
    <SmartParseDrawerHeader
      embedded
      title="MCP Tool"
      eventId={toolName}
      method={rateClass}
      path={endpointId}
      onCopyEventId={handleCopy}
    />
  ) : null;

  // Hero content — status pills + schema hash chip, same slot the
  // WAF Signature drawer uses for its severity/action pills.
  const hero = (
    <Stack spacing={1.5} sx={{ pt: 1 }}>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <RateClassPill cls={rateClass} />
        <StatusPill status={toolStatus} />
        {detail?.risk_class && <LabelPill text={`risk: ${detail.risk_class}`} tone={detail.risk_class === "destructive" ? "danger" : "neutral"} />}
        {detail?.flow_role && <LabelPill text={`flow: ${detail.flow_role}`} tone="neutral" />}
        {(detail?.desc_findings || []).some((f) => f.severity === "high") && <LabelPill text="poisoning findings" tone="danger" />}
        {detail?.input_schema_hash && (
          <Chip
            size="small"
            variant="outlined"
            label={`schema ${shortHash(detail.input_schema_hash)}`}
            sx={{ height: 22, fontSize: 11, borderRadius: 999, borderColor: BORDER_COLOR, fontFamily: tokens.fontMono }}
          />
        )}
      </Stack>
    </Stack>
  );

  const footerActions = (
    <Stack direction="row" spacing={1.5} alignItems="center">
      <Button onClick={onClose} sx={secondaryButtonStyle}>Close</Button>
      <Button
        onClick={handleApprove}
        disabled={approving}
        sx={{ ...primaryButtonStyle, boxShadow: "0 4px 14px rgba(99,113,247,0.30)" }}
      >
        {approving ? "Approving…" : "Add tool to allow-list"}
      </Button>
    </Stack>
  );

  return (
    <WafEventDrawerShell
      open={!!open}
      onClose={onClose}
      title="MCP Tool"
      headerVariant="hero"
      headerContent={header}
      heroContent={hero}
      loading={loading}
      error={error || null}
      footerActions={footerActions}
    >
      {!loading && detail && (
        <>
          <SmartParseSectionCard icon={DETECTION_ICONS.clipboard} title="Schema">
            <Typography sx={{ fontSize: 13, color: tokens.neutral[800] }}>
              {detail.description || "(no description)"}
            </Typography>
            {detail.required_scopes?.length ? (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
                {detail.required_scopes.map((s) => (
                  <span key={s} style={{ fontFamily: tokens.fontMono, fontSize: 11, padding: "1px 8px", borderRadius: 999, background: tokens.neutral[100] }}>
                    {s}
                  </span>
                ))}
              </Stack>
            ) : null}
          </SmartParseSectionCard>

          {/* MCP_ROADMAP_UI_2026_11_03 */}
          <SmartParseSectionCard icon={Shield} title="Tool poisoning scan">
            {(detail.desc_findings || []).length ? (
              <Stack spacing={0.75}>
                {detail.desc_findings.map((f, i) => (
                  <Box key={i} sx={{ fontSize: 12, borderLeft: `3px solid ${f.severity === "high" ? tokens.danger[500] : tokens.neutral[300]}`, pl: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{f.code} · {f.severity} · <code style={{ fontFamily: tokens.fontMono }}>{f.field}</code></Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>{f.detail}</Typography>
                  </Box>
                ))}
                <Typography sx={{ fontSize: 11, color: tokens.neutral[600] }}>
                  High findings keep the tool pending until you allow it. Review the description before allowing.
                </Typography>
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>No injection, hidden-text, secret-path or exfiltration patterns in the description or argument descriptions.</Typography>
            )}
          </SmartParseSectionCard>

          <SmartParseSectionCard icon={DETECTION_ICONS.clipboard} title="Labels">
            <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mb: 0.75 }}>
              Data-flow role — once a session calls a <b>source</b>, calls to <b>sink</b> tools are blocked when the endpoint's taint mode is Enforce.
              {detail.flow_role_suggested && !detail.flow_role ? ` Suggested: ${detail.flow_role_suggested}.` : ""}
            </Typography>
            <ChoiceRow
              value={detail.flow_role || "none"}
              opts={[["none", "None"], ["source", "Sensitive source"], ["sink", "External sink"], ["both", "Both"]]}
              busy={savingLabel === "flow_role"}
              onPick={(v) => setLabel("flow_role", v)}
            />
            <Typography sx={{ fontSize: 12, color: tokens.neutral[700], mt: 1.5, mb: 0.75 }}>
              Risk class — with Human approval on, destructive (or high) tools need an approver for every call.
              {detail.risk_class_suggested && !detail.risk_class ? ` Suggested: ${detail.risk_class_suggested}.` : ""}
            </Typography>
            <ChoiceRow
              value={detail.risk_class || "none"}
              opts={[["none", "Unset"], ["low", "Low"], ["high", "High"], ["destructive", "Destructive"]]}
              busy={savingLabel === "risk_class"}
              onPick={(v) => setLabel("risk_class", v)}
            />
          </SmartParseSectionCard>

          <SmartParseSectionCard icon={Activity} title="24 h calls + block rate">
            <Box sx={{ height: 90, display: "flex", alignItems: "flex-end", gap: "3px" }}>
              {seriesBars.map((b, i) => (
                <Box key={i} sx={{ flex: 1, height: "100%", display: "flex", flexDirection: "column-reverse" }}>
                  <Box sx={{ height: `${b.ok}%`, background: tokens.primaryIndigo }} />
                  <Box sx={{ height: `${b.blk}%`, background: tokens.danger[500] }} />
                </Box>
              ))}
            </Box>
            <Stack direction="row" justifyContent="space-between" sx={{ fontFamily: tokens.fontMono, color: tokens.neutral[600], fontSize: 11, pt: 0.5 }}>
              <span>-24h</span><span>now</span>
            </Stack>
          </SmartParseSectionCard>

          <SmartParseSectionCard icon={User} title={`Last ${callers.length || 0} callers`}>
            {callers.length ? (
              <Box sx={{ border: `1px solid ${BORDER_COLOR}`, borderRadius: 2, overflow: "hidden" }}>
                {callers.map((c, i) => (
                  <Stack key={i} direction="row" alignItems="center" spacing={1}
                    sx={{ p: 1, borderBottom: i < callers.length - 1 ? `1px solid ${BORDER_COLOR}` : "none", fontSize: 12 }}>
                    <Typography sx={{ flex: 1, fontSize: 12 }}>{c.agent_id || "—"}</Typography>
                    <Typography sx={{ fontFamily: tokens.fontMono, color: tokens.primary[300], fontSize: 12 }}>
                      {c.session_fp ? c.session_fp.slice(0, 10) : "—"}
                    </Typography>
                    <Typography sx={{ fontFamily: tokens.fontMono, color: tokens.neutral[700], width: 70, textAlign: "right", fontSize: 12 }}>
                      {fmtAgo(c.ts)}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            ) : (
              <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>No calls in the last 24 h.</Typography>
            )}
          </SmartParseSectionCard>

          <SmartParseSectionCard icon={Shield} title="Attack-class breakdown">
            {attacks.length ? (
              <Stack spacing={0.75}>
                {attacks.map((a) => (
                  <Box key={a.name} sx={{ display: "grid", gridTemplateColumns: "140px 1fr 40px", gap: 1, alignItems: "center", fontSize: 12 }}>
                    <code style={{ fontFamily: tokens.fontMono }}>{a.name}</code>
                    <Box sx={{ height: 8, background: tokens.neutral[100], borderRadius: 999 }}>
                      <Box sx={{ height: "100%", borderRadius: 999, background: tokens.danger[500], width: a.w }} />
                    </Box>
                    <Typography sx={{ textAlign: "right", fontFamily: tokens.fontMono, fontSize: 12 }}>{a.v}</Typography>
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography sx={{ fontSize: 12, color: tokens.neutral[600] }}>No attack-class hits in the last 24 h.</Typography>
            )}
          </SmartParseSectionCard>
        </>
      )}
    </WafEventDrawerShell>
  );
}

// MCP_ROADMAP_UI_2026_11_03
function LabelPill({ text, tone }) {
  const danger = tone === "danger";
  return <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: danger ? "#FBE8E8" : tokens.neutral[100], color: danger ? "#8E1F1F" : tokens.neutral[800] }}>{text}</span>;
}

function ChoiceRow({ value, opts, busy, onPick }) {
  return (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
      {opts.map(([v, label]) => (
        <Chip
          key={v}
          size="small"
          label={label}
          disabled={busy}
          onClick={() => v !== value && onPick(v)}
          variant={v === value ? "filled" : "outlined"}
          sx={{
            height: 26, fontSize: 12, borderRadius: 999,
            ...(v === value ? { background: tokens.primaryIndigo, color: "#fff" } : { borderColor: BORDER_COLOR }),
          }}
        />
      ))}
    </Stack>
  );
}

function RateClassPill({ cls }) {
  const s = RATE_CLASS_STYLE[cls] || RATE_CLASS_STYLE.normal;
  return <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: s.bg, color: s.fg }}>{cls}</span>;
}
function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.active;
  return <span style={{ fontSize: 11, padding: "1px 8px", borderRadius: 999, background: s.bg, color: s.fg }}>{status}</span>;
}

// ── Page ─────────────────────────────────────────────────────────────

export default function McpTools() {
  const { effectiveSiteUid } = useGlobalSite();
  const [activeEp, setActiveEp] = useState(null);
  const [drawerName, setDrawerName] = useState(null);

  // 1) Endpoint list — picks default = first non-disabled.
  const epPath = effectiveSiteUid ? `mcp/sites/${effectiveSiteUid}/endpoints` : null;
  const { data: epResp, error: epError, loading: epLoading } = useMcpQuery(epPath, { skip: !effectiveSiteUid });
  const endpoints = useMemo(() => {
    const items = epResp?.items || epResp?.data || [];
    return items.filter((e) => e.state !== "disabled");
  }, [epResp]);

  useEffect(() => {
    if (!activeEp && endpoints.length) setActiveEp(endpoints[0].endpoint_id);
  }, [endpoints, activeEp]);

  // 2) Tools for the active endpoint.
  const toolsPath = effectiveSiteUid && activeEp
    ? `mcp/sites/${effectiveSiteUid}/tools?endpoint=${activeEp}&limit=200`
    : null;
  const {
    data: toolsResp,
    error: toolsError,
    loading: toolsLoading,
    refetch: refetchTools,
  } = useMcpQuery(toolsPath, { skip: !toolsPath });
  const tools = toolsResp?.data || [];

  // 3) Derived metrics.
  const metrics = useMemo(() => {
    const pending = tools.filter((t) => t.status === "pending_approval").length;
    const write = tools.filter((t) => String(t.rate_class || "").toLowerCase() === "high" || /write|update|rotate|refund|merge/.test(t.name || "")).length;
    const blocked = tools.reduce((s, t) => s + (Number(t.calls_24h) ? 0 : 0), 0); // placeholder — CH does not expose per-tool blocks separately in listTools; show 0 until CP returns it
    return [
      { label: "Tools exposed", value: String(tools.length), sub: `endpoint ${activeEp ? activeEp.slice(0, 8) : "—"}` },
      { label: "Pending approval", value: String(pending), sub: "schema drift", fg: tokens.secondary[500] },
      { label: "Write-class tools", value: String(write), sub: "rpc + mutations" },
      { label: "Blocked 24 h", value: String(blocked), sub: "scope / rate / drift", fg: tokens.danger[600] },
    ];
  }, [tools, activeEp]);

  return (
    <>
      <Helmet title="MCP Security — Tools" />
      <Stack spacing={3} sx={{ p: 6 }}>
        <V2PageHeader
          title="MCP Security — Tool catalog"
          description="Per-endpoint tools the model is allowed to call, with runtime activity."
        />
        <SiteScopeGate mode="any">
          {() => (
            <>
              {!effectiveSiteUid && (
                <Alert severity="info">Select a site from the header to see the tool catalog.</Alert>
              )}
              {epError && <Alert severity="error">Endpoint list failed: {epError}</Alert>}
              {toolsError && <Alert severity="error">Tools failed: {toolsError}</Alert>}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                  gap: 1.5,
                }}
              >
                {metrics.map((m) => (
                  <MetricCard key={m.label} label={m.label} value={m.value} sub={m.sub} />
                ))}
              </Box>

              <Box
                sx={{
                  background: "#fff",
                  border: `1px solid ${tokens.neutral[200]}`,
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  sx={{ p: 1.75, borderBottom: `1px solid ${tokens.neutral[200]}`, flexWrap: "wrap" }}
                >
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography sx={{ fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: tokens.neutral[1000] }}>Tool manifest</Typography>
                    <Typography sx={{ fontSize: 12, color: tokens.neutral[700] }}>
                      Per-endpoint tools the model is allowed to call, with 24 h runtime activity.
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", border: `1px solid ${tokens.neutral[200]}`, borderRadius: "8px", overflow: "hidden" }}>
                    {endpoints.length === 0 && !epLoading && (
                      <Typography sx={{ p: 1, fontSize: 12, color: tokens.neutral[700] }}>
                        No MCP endpoints registered yet.
                      </Typography>
                    )}
                    {endpoints.map((ep) => (
                      <Box
                        key={ep.endpoint_id}
                        onClick={() => setActiveEp(ep.endpoint_id)}
                        sx={{
                          height: 34,
                          px: 1.5,
                          display: "grid",
                          placeItems: "center",
                          fontSize: 12,
                          fontFamily: tokens.fontMono,
                          cursor: "pointer",
                          background: activeEp === ep.endpoint_id ? tokens.primaryIndigo : "#fff",
                          color: activeEp === ep.endpoint_id ? "#fff" : tokens.neutral[800],
                        }}
                      >
                        {ep.location_pattern || ep.endpoint_url || ep.endpoint_id.slice(0, 8)}
                      </Box>
                    ))}
                  </Box>
                </Stack>

                {(epLoading || toolsLoading) && <LinearProgress />}

                <Box sx={{ overflowX: "auto" }}>
                  <Box sx={{ minWidth: 1120 }}>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "minmax(150px,1.3fr) 90px 90px 130px 90px 70px 100px 110px 110px",
                        gap: "0 12px",
                        px: 2,
                        py: 1.25,
                        background: tokens.neutral[50],
                        borderBottom: `1px solid ${tokens.neutral[300]}`,
                        fontSize: 11,
                        fontWeight: 500,
                        color: tokens.neutral[700],
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      <span>Name</span>
                      <span>Rate class</span>
                      <span>Budget rpm</span>
                      <span>Scopes</span>
                      <span>Calls 24 h</span>
                      <span>p95 ms</span>
                      <span>Last called</span>
                      <span>Status</span>
                      <span>Schema hash</span>
                    </Box>
                    {tools.length === 0 && !toolsLoading && activeEp && (
                      <Box sx={{ p: 3, textAlign: "center", color: tokens.neutral[700], fontSize: 13 }}>
                        No tools reported for this endpoint yet.
                      </Box>
                    )}
                    {tools.map((t) => {
                      const scopes = (t.required_scopes || []).join(", ") || "—";
                      const pending = t.status === "pending_approval";
                      return (
                        <Box
                          key={t.tool_id}
                          onClick={() => setDrawerName(t.name)}
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "minmax(150px,1.3fr) 90px 90px 130px 90px 70px 100px 110px 110px",
                            gap: "0 12px",
                            px: 2,
                            height: 44,
                            alignItems: "center",
                            borderBottom: `1px solid ${tokens.neutral[150]}`,
                            fontSize: 13,
                            cursor: "pointer",
                            background: pending ? "rgba(255,219,67,0.08)" : "#fff",
                            "&:hover": { background: tokens.neutral[50] },
                          }}
                        >
                          <span style={{ fontFamily: tokens.fontMono, fontWeight: 500, display: "flex", gap: 6, alignItems: "center" }}>
                            {t.name}
                            {t.desc_max_severity === "high" && <LabelPill text="poisoning" tone="danger" />}
                            {t.risk_class === "destructive" && <LabelPill text="destructive" tone="danger" />}
                          </span>
                          <span><RateClassPill cls={t.rate_class} /></span>
                          <span style={{ fontFamily: tokens.fontMono }}>{t.rate_budget_rpm}/min</span>
                          <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[800] }}>{scopes}</span>
                          <span style={{ fontFamily: tokens.fontMono }}>{fmtInt(t.calls_24h)}</span>
                          <span style={{ fontFamily: tokens.fontMono, color: tokens.neutral[700] }}>{fmtMs(t.p95_ms)}</span>
                          <span style={{ color: tokens.neutral[700] }}>{fmtAgo(t.last_called_at)}</span>
                          <span><StatusPill status={t.status} /></span>
                          <span style={{ fontFamily: tokens.fontMono, fontSize: 12, color: tokens.neutral[700] }}>{shortHash(t.input_schema_hash)}</span>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              </Box>
            </>
          )}
        </SiteScopeGate>
      </Stack>

      <ToolDrawer
        open={!!drawerName}
        siteUid={effectiveSiteUid}
        endpointId={activeEp}
        toolName={drawerName}
        onClose={() => setDrawerName(null)}
        onApproved={refetchTools}
      />
    </>
  );
}
